import { useEffect, useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useMembershipStore } from '../stores/membershipStore';

/**
 * 前端权限 key（camelCase）。对外接口保持不变，避免改动所有调用点。
 * 服务端下发的功能权限为 snake_case（见后端 seed 的 feature_key），
 * 通过 FEATURE_KEY_MAP 映射后判定，实现「权限由服务端统一驱动」。
 */
export type PermissionKey =
  | 'exportCsv'
  | 'exportPrint'
  | 'shareOrder'
  | 'advancedAnalytics'
  | 'dataBackup'
  | 'orderOffset'
  | 'manageUsers'
  | 'manageOrders'
  | 'manageCustomers'
  | 'manageSettings';

/** 免费等级 code（后端 MembershipLevel.code 的默认等级） */
const FREE = 'free';

/** 前端 camelCase key → 后端 snake_case feature_key 映射 */
const FEATURE_KEY_MAP: Record<PermissionKey, string> = {
  exportCsv: 'export_csv',
  exportPrint: 'export_print',
  shareOrder: 'share_order',
  advancedAnalytics: 'advanced_analytics',
  dataBackup: 'data_backup',
  orderOffset: 'order_offset',
  // 管理类为角色权限，非会员功能项；服务端 features 不含这些 key，仅管理员经 isAdmin 放行
  manageUsers: 'manage_users',
  manageOrders: 'manage_orders',
  manageCustomers: 'manage_customers',
  manageSettings: 'manage_settings',
};

/**
 * 管理员判定：以后端下发的 isAdmin 为准（后端 isAdminRole 同时接受 'admin' 与历史值 '管理员'）。
 * 回落到本地比对 role 只为兼容升级前持久化的旧会话数据，那种数据里没有 isAdmin 字段。
 * `usePermission` 与点击那一刻的会员门禁（`utils/paywall`）共用这一份，两处各写必然漂移。
 */
export function isAdminViewer(
  user?: { isAdmin?: boolean; role?: string | null } | null,
  profile?: { isAdmin?: boolean; role?: string | null } | null,
): boolean {
  const rawRole = user?.role ?? profile?.role ?? null;
  return user?.isAdmin ?? profile?.isAdmin ?? (rawRole === 'admin' || rawRole === '管理员');
}

/**
 * 功能位判定本体。`can()` 与点击那一刻的门禁都走它——
 * 映射表只在这里读一次，两处各读各的就会有一处漏掉 key 改名。
 */
export function featureAllowed(isAdmin: boolean, features: string[], key: PermissionKey): boolean {
  if (isAdmin) return true;
  return features.includes(FEATURE_KEY_MAP[key] ?? key);
}

export interface PermissionResult {
  /** 是否为生效中的付费会员（等级非 free 且未过期）——等价于服务端 isActive */
  isPremium: boolean;
  isExpired: boolean;
  /** 管理员判定以后端下发的 isAdmin 为准，前端不要再比对 role 字符串 */
  isAdmin: boolean;
  can: (key: PermissionKey) => boolean;
  /** 当前等级 code（如 free / premium），由服务端下发 */
  levelCode: string;
  /** 等级显示名，由后台配置下发；会员状态尚未加载完成时为空串 */
  levelName: string;
  expireAt: Date | null;
  daysLeft: number | null;
  autoRenew: boolean;
  /** 服务端下发的生效功能权限 key 列表（snake_case） */
  features: string[];
}

/**
 * 权限判定 hook（服务端驱动）。
 *
 * - 会员状态（等级/到期/生效功能）来自 GET /membership/status，存于 membershipStore，
 *   首次调用时按当前用户 id 触发加载；未加载完成前回落到 profile 的基础判定，避免闪烁误判。
 * - can(key)：管理员放行全部；其余按服务端 features 是否包含映射后的 feature_key 判定。
 * - 不再硬编码 PREMIUM_PERMISSIONS / ADMIN_PERMISSIONS。
 */
export function usePermission(): PermissionResult {
  const { profile, user } = useAuthStore();
  const status = useMembershipStore((s) => s.status);
  const load = useMembershipStore((s) => s.load);
  const reset = useMembershipStore((s) => s.reset);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (userId) void load(userId);
    else reset();
  }, [userId, load, reset]);

  return useMemo(() => {
    const isAdmin = isAdminViewer(user, profile);

    const levelCode = status?.levelCode ?? profile?.userLevel ?? FREE;
    const features = status?.features ?? [];

    const expireAt = status?.expireAt
      ? new Date(status.expireAt)
      : profile?.userLevelExpireAt
        ? new Date(profile.userLevelExpireAt)
        : null;

    const isExpired = expireAt ? expireAt < new Date() : false;
    const isPremium = status ? status.isActive : levelCode !== FREE && !isExpired;
    // 剩余天数只认服务端那一份：`daysRemaining` 按业务日历日相减（与到期日同一口径），
    // 本地再按毫秒差取整会多报一天，且与到期提醒任务的文案互相矛盾。状态没加载完就是 null，
    // 展示层据此省略「剩余 N 天」而不是猜一个。
    const daysLeft = status?.daysRemaining ?? null;

    const can = (key: PermissionKey): boolean => featureAllowed(isAdmin, features, key);

    return {
      isPremium,
      isExpired,
      isAdmin,
      can,
      levelCode,
      // 等级名由后台配置，未加载完成前不给任何兜底名——硬编码「尊享会员」会和后台改名后的配置对不上
      levelName: status?.levelName ?? '',
      expireAt,
      daysLeft,
      autoRenew: status?.autoRenew ?? false,
      features,
    };
  }, [
    user?.isAdmin,
    user?.role,
    profile?.isAdmin,
    profile?.role,
    profile?.userLevel,
    profile?.userLevelExpireAt,
    status,
  ]);
}
